import {BookAppointmentRequest, BookClassRequest, GetFulfillmentStatusRequest, space} from "../../compiled";
import {useStripeFulfillmentRpc} from "./RpcHooks/StripeFulfillmentHook";
import {useBookClassRpc} from "./RpcHooks/BookClassHook";
import {App2} from "../App2";
import {useBookAppointmentRpc} from "./RpcHooks/BookAppointmentHook";
import BookingPurchaseOption = space.kenko.proto.BookingPurchaseOption;
import {useSearchParams} from "react-router-dom";
import {useAuth0} from "@auth0/auth0-react";
import {useEffect} from "react";

/**
 * Handles the stripe purchase callback. This looks up the stripe session
 * on the server to see if the payment succeeded. This also triggers fulfillment
 * of the purchase (same as the webhook).
 *
 * Once the credit or package has been successfully fulfilled, it proceeds
 * to book the appointment.
 *
 * Once booked, it goes to the booking confirmation page.
 */
export const StripeCallback = () => {

  const {user, isLoading} = useAuth0()

  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('s');
  
  /**
   * Fulfillment happens first; this checks which thing (credit or package) was fulfilled
   * by the stripe purchase. It possibly triggers fulfillment if the webhook has for some
   * reason not executed.
   */
  const fulfillmentRpc = useStripeFulfillmentRpc()

  /**
   * One of the two is used here...
   */
  const bookClassRpc = useBookClassRpc()
  const bookAppointmentRpc = useBookAppointmentRpc()

  useEffect(() => {
    
    if (isLoading) {
      return;
    }
    
    // Don't send this RPC until the user is loaded. This is a return from stripe
    // so the user will take a second to load. If we do try while the user is not loaded
    // we'll get a 401 unauthorized and try to redirect to google to re-login.
    fulfillmentRpc.send(new GetFulfillmentStatusRequest({
      sessionId: sessionId
    }))
    
  }, [user, isLoading]);
  
  const findStripeMetadata = (key: string) => {
    if (!fulfillmentRpc.response?.fulfillment?.stripeMetadata) {
      return null;
    }

    for (let i = 0; i < fulfillmentRpc.response?.fulfillment?.stripeMetadata?.length; i++) {
      if (fulfillmentRpc.response?.fulfillment?.stripeMetadata[i]?.key === key) {
        return fulfillmentRpc.response?.fulfillment?.stripeMetadata[i]?.value;
      }
    }

    return null;
  }


  const Content = () => {
    if (fulfillmentRpc.rpcState.state === 'pending') {
      return <div>Checking payment status...</div>
    }

    if (fulfillmentRpc.rpcState.state === 'error') {
      return <>
        <div>Error validating your payment. You can try to refresh this page
          or contact support at
          <a href={'mailto:support@onkensu.com'}>support@onkensu.com</a>
        </div>
        <div>Error status: {fulfillmentRpc.rpcState.message}</div>
      </>
    }

    if (fulfillmentRpc.rpcState.state === 'success') {

      let purchaseOption: BookingPurchaseOption;

      if (fulfillmentRpc.response?.fulfillment?.purchasedPackage) {
        purchaseOption = new BookingPurchaseOption({
          prepaidPackageId: fulfillmentRpc.response.fulfillment.purchasedPackage.purchasedClassPackId,
        });
      } else if (fulfillmentRpc.response?.fulfillment?.purchasedCredit) {
        purchaseOption = new BookingPurchaseOption({
          prepaidSingleCreditId: fulfillmentRpc.response.fulfillment.purchasedCredit.classCreditId,
        })
      } else {
        throw new Error("Non supported purchase option");
      }


      const businessId = findStripeMetadata("businessId");
      const eventInstanceId = findStripeMetadata("eventInstanceId");
      const serviceId = findStripeMetadata("serviceId");

      if (eventInstanceId && bookClassRpc.rpcState.state == 'none') {

        bookClassRpc.send(new BookClassRequest({
          businessId: findStripeMetadata("businessId"),
          eventInstanceId: findStripeMetadata("eventInstanceId"),
          purchaseOption: purchaseOption,
        }));

      } else if (serviceId && bookAppointmentRpc.rpcState.state == 'none') {
        bookAppointmentRpc.send(new BookAppointmentRequest({
          businessId: businessId,
          serviceId: serviceId,
          locationId: findStripeMetadata("serviceLocationId"),
          virtualAppt: findStripeMetadata("serviceIsVirtual") === "true",
          appointmentTime: parseInt(findStripeMetadata("serviceAppointmentTime") as string),
          practitionerId: findStripeMetadata("servicePractitionerId"),
          purchaseOption: purchaseOption
        }))
      } else {
        console.log('Error: Could not find serviceId or eventInstanceId in stripe metadata');
      }
    }

    return <>

      {bookClassRpc.rpcState.state == 'pending' &&
          <div>Payment validated. We're attempting to book your class now.</div>}
      {bookClassRpc.rpcState.state == 'error' && <>
        <div>There was an error booking your class. We're sorry!</div>

        <div>
          The credit or package you purchased is on your account and can
          be used to retry this booking.
        </div>

        <div className={'ErrorText'}>Error: {bookClassRpc.rpcState.message}</div>
      </>}


      {bookAppointmentRpc.rpcState.state == 'pending' &&
          <div>Payment validated. We're attempting to book your appointment now.</div>}
      {bookAppointmentRpc.rpcState.state == 'error' && <>
        <div>There was an error booking your appointment. We're sorry!</div>

        <div>
          The credit or package you purchased has been added to your account and can
          be used to retry this booking or book a different one.
        </div>

        <div className={'ErrorText'}>Error: {bookAppointmentRpc.rpcState.message}</div>
      </>}
    </>
  }

  return <App2>
    <div style={{margin: 50, display: 'flex', flexDirection: "column", gap: 15}}>
      <Content/>
    </div>
  </App2>
}